import CloseIcon from '@mui/icons-material/Close';
import { Box, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ICandidate, IJob, getCandidateSubmissionSettingsTemplate } from 'wisestep-api';
import {
  WsDialog,
  WsDialogSize,
  WsColors,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
} from 'wisestep-base';

import { replacePlaceHoldersEmailBody } from '../../../components/BulkEmail/helper/apiCalls';
import { useWorkspaceContext } from '../../../context';
import ErrorLogger from '../../../providers/ErrorLogger';

import EmailPreview from './EmailPreview';
import SubmissionAssign from './SubmissionAssign';

interface InternalSubmissionProp {
  open: boolean;
  onClose: () => void;
  job?: IJob | null;
  candidateSubmittedData: ICandidate | null;
  onSubmit: (recruiters: any, comment: string) => void;
}

const dialogTitleContainerSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  p: 2,
  bgcolor: WsColors.BorderGrey,
};

const InternalSubmission = ({
  open,
  onClose,
  job,
  candidateSubmittedData,
  onSubmit,
}: InternalSubmissionProp) => {
  const [step, setStep] = useState<number>(1);
  const [emailBody, setEmailBody] = useState<string | null>(null);
  const [emailSubject, setEmailSubject] = useState<string | null>(null);
  const [isEmailDataLoaded, setIsEmailDataLoaded] = useState<boolean>(true);
  const { id } = useWorkspaceContext();

  const getEmailData = useCallback(
    async (candidateInfo: ICandidate) => {
      try {
        setIsEmailDataLoaded(false);
        const setting = await getCandidateSubmissionSettingsTemplate(
          job?.submissionFormTemplete ?? '',
          id ?? ''
        );
        const { updatedSubject, updatedBody } = await replacePlaceHoldersEmailBody(
          setting?.data,
          candidateInfo,
          (job as IJob) ?? {},
          false
        );
        setEmailSubject(updatedSubject ?? '');
        setEmailBody(updatedBody ?? '');
      } catch (error) {
        console.error('Error fetching submission settings:', error);
      } finally {
        setIsEmailDataLoaded(true);
      }
    },
    [job, id]
  );

  useEffect(() => {
    candidateSubmittedData && getEmailData(candidateSubmittedData);
  }, [candidateSubmittedData, getEmailData]);

  const handleAssignSubmit = (data: { recruiters: any; comment: string }) => {
    onSubmit(data.recruiters, data.comment);
    onClose();
  };

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <WsDialog openDialog={open} size={WsDialogSize.MEDIUM}>
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={dialogTitleContainerSx}>
            <Box display="flex" flexDirection="column">
              <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK} bold>
                Submit Internally
              </WsTypography>

              <Box mt={0.5}>
                <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.GREYTEXT}>
                  Step {step} of 2 - {step === 1 ? 'Preview email' : 'Assign internal user'}
                </WsTypography>
              </Box>
            </Box>

            <IconButton onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {step === 1 ? (
            <EmailPreview
              emailBody={emailBody}
              emailSubject={emailSubject}
              isLoaded={isEmailDataLoaded}
              onProceed={() => setStep(2)}
            />
          ) : (
            <SubmissionAssign onBack={() => setStep(1)} onSubmit={handleAssignSubmit} />
          )}
        </DialogContent>
      </WsDialog>
    </ErrorBoundary>
  );
};

export default InternalSubmission;
